import { useEffect, useRef, useState } from 'react';
import { useGame } from '../state/SocketProvider';
import { Contours } from '../ui/Contours';
import { helpStore } from '../state/help';
import { studioStore } from '../state/studio';
import { historyStore } from '../state/history';
import { simStore } from '../state/sim';
import type { Settings } from '../../shared/constants';
import { createSettings } from '../state/settingsPrefsStore';
import { PROTOCOL_VERSION } from '../../shared/constants';
import { Explainer } from './Explainer'; // v28 BOLT-156

// v14 BOLT-046: no name field here — the fastest path is INTO the lobby.
// create/join carry the sticky name (set by the lobby's rename) or defaults.
export function Landing() {
  // v22 BOLT-108: every deliberate entry action goes through `startFresh` —
  // it clears a stale resume token (which used to silently rejoin an OLD
  // session instead) and queues, so a still-connecting socket can't eat it.
  const { startFresh, status, lastError } = useGame();
  const params = new URLSearchParams(location.search);
  const [code, setCode] = useState((params.get('code') || '').toUpperCase());

  // v28 BOLT-157 (Jake, from the field): "scanning opens the web app up to the
  // home page and places the code in the code form field, but doesn't put the
  // user directly into the lobby."
  //
  // Right: `?code=` has only ever PREFILLED. Somebody who scans a QR or taps a
  // shared link has already said which room they want, so asking them to press
  // Join again is a step with no decision in it - and it is the same step that
  // makes a scan feel like it did not work.
  //
  // THE PARAM IS CONSUMED, not just read. Landing remounts every time a player
  // leaves a lobby, so a `?code=` still sitting in the URL would auto-join them
  // straight back into the room they just left, with no way out. Stripping it
  // on use also makes a refresh behave: the code stays in the field, and the
  // player decides.
  //
  // `startFresh` (v22 BOLT-108) is what makes this safe on a cold load: it
  // drops a stale resume token, which used to hijack a deliberate join, and it
  // QUEUES, so a still-connecting socket cannot eat the message.
  const autoJoined = useRef(false);
  useEffect(() => {
    if (autoJoined.current) return;
    const scanned = (new URLSearchParams(location.search).get('code') || '').trim().toUpperCase();
    if (scanned.length < 4) return;
    autoJoined.current = true;
    history.replaceState(null, '', location.pathname);
    startFresh({ t: 'join', code: scanned, name: 'Player', protocolVersion: PROTOCOL_VERSION });
  }, [startFresh]);

  const ready = status === 'open';
  // Optional settings override (advanced setup seam; the E2E harness uses it for
  // deterministic decks + short veto/curse windows). Undefined in normal use.
  const testSettings = (window as unknown as { __boltSettings?: Record<string, number> }).__boltSettings;
  const create = () => {
    // v29 BOLT-142 (Jake): "if a player ever edits a card or a game setting,
    // that setting should be persisted for that user for future games that they
    // create." This is the ONE place prefs are read - never into a live room,
    // which is what keeps a stored value from ever outranking a deliberate act
    // (the v22 BOLT-103 failure). `testSettings` still wins: it is deliberate.
    startFresh({
      t: 'create',
      name: 'Host',
      settings: createSettings(testSettings as Partial<Settings> | undefined),
      protocolVersion: PROTOCOL_VERSION,
    });
  };
  // v19 BOLT-086: Practice — a solo round vs the server bot. Turn on the local
  // simulator (tap the map to walk) seeded inside the demo boundary, then create
  // a practice session. No second device, no real GPS needed.
  const practice = () => {
    simStore.enable({ lat: 33.1556, lng: -96.7214 }); // a start inside the practice boundary
    startFresh({ t: 'create', name: 'You', protocolVersion: PROTOCOL_VERSION, practice: true });
  };
  const join = () => {
    if (code.trim().length >= 4) {
      startFresh({
        t: 'join',
        code: code.trim().toUpperCase(),
        name: 'Player',
        protocolVersion: PROTOCOL_VERSION,
      });
    }
  };

  return (
    <div className="screen">
      <Contours />
      <div className="screen__inner">
        <div className="landing-hero">
          {/* v22 BOLT-112: the differentiator vs offline hide and seek is the
              STRATEGY layer, so that is the positioning line. The tagline is
              the mission, not an explainer. "Neighborhood" is gone: it is
              wrong for a park, a campus, or downtown. */}
          <div className="eyebrow">Fast-paced, strategic hide &amp; seek</div>
          <div className="wordmark" style={{ marginTop: '0.6rem' }}>
            Bolt
          </div>
          <div className="tagline">
            Get outside, <strong>together.</strong>
          </div>
        </div>

        {/* v28 BOLT-156 (Jake): "an actual code based scripted game play on the
            home page… very effective at explaining the general concept."
            It sits under the hero and above the entry actions: it is what the
            game IS, and a player who already knows scrolls past it in one
            thumb-flick. It holds no socket and cannot start anything. */}
        <Explainer />

        {/* v22 BOLT-111: ONE screen. Roughly half of all players are second
            players, and Home used to lead hard with Create and bury the code
            behind a button that opened a whole second view, so joiners
            mis-tapped Create and could not find where to type. Create and Join
            are peers now, both here, no navigation. */}
        <div className="landing-actions">
          <button
            className="btn btn--signal btn--lg"
            disabled={!ready}
            onClick={create}
            data-testid="create-btn"
          >
            Create a game
          </button>
          <div className="divider">or</div>
          <div className="join-row">
            <label className="field" style={{ flex: 1, margin: 0 }}>
              <span className="field__label">Game code</span>
              <input
                className="input input--code"
                placeholder="6 chars"
                value={code}
                maxLength={6}
                inputMode="text"
                autoCapitalize="characters"
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') join();
                }}
                data-testid="code-input"
              />
            </label>
            <button
              className="btn btn--lg"
              disabled={!ready || code.trim().length < 4}
              onClick={join}
              data-testid="join-btn"
            >
              Join
            </button>
          </div>

          {!ready && (
            <div className="eyebrow" style={{ textAlign: 'center', marginTop: '0.4rem' }}>
              Connecting to server…
            </div>
          )}
          {lastError && (
            <div className="banner banner--signal" data-testid="landing-error">
              <span className="banner__detail">{lastError.message}</span>
            </div>
          )}
        </div>

        {/* v15 BOLT-058: the reference surfaces sink — entry actions own the
            fold; How to Play / History / Studio wait quietly at the bottom.
            v22 BOLT-111: Practice Solo joins them. The top section is PRIMARY
            CTAs ONLY (Create + Join); practice is a quieter, tertiary action,
            not a top-line choice (Jake, 2026-07-28). */}
        <div className="landing-footer" style={{ marginTop: 'auto', paddingTop: '2rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <button className="btn btn--ghost" disabled={!ready} onClick={practice} data-testid="practice-btn">
            Practice solo
          </button>
          <button className="btn btn--ghost" data-testid="howto-open" onClick={() => helpStore.open()}>
            How to play
          </button>
          <button className="btn btn--ghost" data-testid="history-open" onClick={() => historyStore.open()}>
            History
          </button>
          <button className="btn btn--ghost" data-testid="studio-open" onClick={() => studioStore.open()}>
            Card Studio
          </button>
        </div>
      </div>
    </div>
  );
}
